import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Webhook } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const FAV = (d) => `https://www.google.com/s2/favicons?domain=${d}&sz=128`;

const INTEGRATIONS = [
    { name: "PayPal", slug: "paypal", fav: FAV("paypal.com") },
    { name: "AWS S3", slug: "amazons3", fb: "S3", fav: FAV("aws.amazon.com") },
    { name: "Azure Blob Storage", slug: "microsoftazure", fb: "Az", fav: FAV("azure.microsoft.com") },
    { name: "Cloudflare", slug: "cloudflare", fav: FAV("cloudflare.com") },
    { name: "Dropbox", slug: "dropbox", fav: FAV("dropbox.com") },
    { name: "FTP / SFTP", fb: "FTP" },
    { name: "Google BigQuery", slug: "googlebigquery", fb: "BQ", fav: FAV("cloud.google.com") },
    { name: "Google Workspace", slug: "googleworkspace", fb: "GW", fav: FAV("workspace.google.com") },
    { name: "Microsoft 365", slug: "microsoft365", fb: "M365", fav: FAV("microsoft365.com") },
    { name: "Monday.com", slug: "mondaydotcom", fav: FAV("monday.com") },
    { name: "NetSuite", fb: "N" },
    { name: "Paycom", fb: "Pc" },
    { name: "QuickBooks", slug: "quickbooks", fb: "QB", fav: FAV("quickbooks.intuit.com") },
    { name: "Sage", slug: "sage", fav: FAV("sage.com") },
    { name: "SendGrid", slug: "sendgrid", fav: FAV("sendgrid.com") },
    { name: "Stripe", slug: "stripe", fav: FAV("stripe.com") },
    { name: "Twilio", slug: "twilio", fav: FAV("twilio.com") },
    { name: "Zoho", slug: "zoho", fav: FAV("zoho.com") },
    { name: "Custom Integrations", fb: "API" },
];

const LogoItem = ({ item, index }) => {
    // 0 = brand SVG, 1 = company favicon, 2 = letter tile
    const [stage, setStage] = useState(item.slug ? 0 : 2);
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45, ease: EASE, delay: (index % 6) * 0.06 }}
            className="group flex w-full flex-col items-center gap-3 py-4"
            data-testid={`integration-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
            <div className="flex h-14 w-14 items-center justify-center transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-105">
                {stage < 2 ? (
                    <img
                        src={stage === 0 ? `https://cdn.simpleicons.org/${item.slug}` : item.fav}
                        alt={`${item.name} logo`}
                        className="h-10 w-10 object-contain"
                        loading="lazy"
                        onError={() => setStage((s) => s + 1)}
                    />
                ) : item.name === "Custom Integrations" ? (
                    <Webhook className="h-9 w-9 text-[#0066FF]" strokeWidth={1.5} />
                ) : (
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#0066FF]/15 bg-gradient-to-br from-[#F0F9FF] to-[#FAF5FF] font-display text-sm font-bold text-[#0066FF]">
                        {item.fb || item.name[0]}
                    </span>
                )}
            </div>
            <p className="text-center text-xs font-medium text-[#64748B] transition-colors duration-300 group-hover:text-[#0F172A]">
                {item.name}
            </p>
        </motion.div>
    );
};

export default function Integrations() {
    return (
        <section
            className="relative bg-[#F6F9FE]"
            style={{
                backgroundImage:
                    "radial-gradient(circle at 8% 8%, rgba(0,229,255,0.05) 0%, transparent 40%), radial-gradient(circle at 95% 92%, rgba(168,85,247,0.05) 0%, transparent 40%), linear-gradient(to right, rgba(15,23,42,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.03) 1px, transparent 1px)",
                backgroundSize: "auto, auto, 44px 44px, 44px 44px",
            }}
            data-testid="integrations-section"
            aria-labelledby="integrations-heading"
        >
            <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE }}
                    >
                        <span
                            className="inline-flex items-center gap-2 rounded-full border border-[#0F172A]/10 bg-white px-4 py-1.5 shadow-sm"
                            data-testid="integrations-eyebrow"
                        >
                            <span className="h-1.5 w-1.5 rounded-full bg-[#00E5FF]" />
                            <span className="font-mono-brand text-[10px] uppercase tracking-[0.3em] text-[#475569] sm:text-xs">
                                Integration Ecosystem
                            </span>
                        </span>
                        <h2
                            id="integrations-heading"
                            className="mt-6 font-display text-4xl font-bold leading-[1.12] tracking-tight text-[#0F172A] sm:text-5xl"
                            data-testid="integrations-heading"
                        >
                            Plug into your
                            <br />
                            <span className="text-brand-gradient">existing tech stack</span>
                        </h2>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
                    >
                        <div className="rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] p-[1.5px]">
                            <button
                                type="button"
                                className="group inline-flex items-center gap-3 rounded-full bg-[#0B1220] py-2 pl-6 pr-2 text-xs font-bold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.03]"
                                data-testid="explore-integrations-button"
                            >
                                Explore All Integrations
                                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-transform duration-300 group-hover:rotate-45">
                                    <ArrowUpRight className="h-4 w-4" />
                                </span>
                            </button>
                        </div>
                    </motion.div>
                </div>

                <div className="mt-14 flex flex-wrap justify-center" data-testid="integrations-grid">
                    {INTEGRATIONS.map((item, i) => (
                        <div key={item.name} className="w-1/2 sm:w-1/3 lg:w-1/6">
                            <LogoItem item={item} index={i} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
